import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { httpAuth } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: undefined = checking, null = not authed, object = authed
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await httpAuth.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Only probe /auth/me when (a) the path is in admin scope, OR
    // (b) we previously set a session hint on login. On all other public
    // pages we skip the call — avoids a guaranteed 401 on every pageview
    // (which browsers log to the console regardless of how we handle it in JS).
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const sessionHint =
      typeof window !== "undefined" &&
      window.sessionStorage?.getItem("bd_session") === "1";
    if (path.startsWith("/admin") || sessionHint) {
      refresh();
    } else {
      setUser(null);
    }
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await httpAuth.post("/auth/login", { email, password });
    setUser(data.user);
    try {
      window.sessionStorage?.setItem("bd_session", "1");
    } catch { /* storage disabled — safe to ignore */ }
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await httpAuth.post("/auth/logout");
    } finally {
      setUser(null);
      try {
        window.sessionStorage?.removeItem("bd_session");
      } catch { /* storage disabled */ }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function formatApiError(detail, fallback = "Something went wrong. Please try again.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
